import React from 'react';
import { Config } from '@/types';
import {
  Input,
  Textarea,
  Select,
  FormGroup,
  Radio,
  Date,
  RangeSlider,
} from '@/components/ui/FormElements';
import { PhoneInput } from '@/components/ui';
import { useWidgetStore } from '@/hooks/stores';
import './styles.scss';

// New React Field Component
interface FieldComponentProps extends Config.Field {
  index?: number;
  value?: string;
  error?: string;
  disabled?: boolean;
  formValues?: Record<string, string>;
  onChange?: (name: string, value: string) => void;
  onBlur?: (name: string) => void;
}

export const FieldComponent: React.FC<FieldComponentProps> = ({
  index,
  name,
  label,
  type,
  enabled = true,
  required = false,
  placeholder = '',
  unsureCheckboxText,
  options = [],
  rows,
  value = '',
  error,
  disabled = false,
  independentMinMax,
  measure,
  onChange,
  onBlur,
}) => {
  if (!enabled) return null;

  // Get user's detected country code from store
  const userCountryCode = useWidgetStore(
    state => state.location.userCountryCode
  );

  const fieldId = `peptalk-${name}`;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    onChange?.(name, e.target.value);
  };

  const handlePhoneChange = (value: string) => {
    onChange?.(name, value);
  };

  switch (type) {
    case 'hidden':
    case 'function':
      // Hidden and function fields don't render any UI but are included in form values
      return null;

    case 'tel':
      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <PhoneInput
            id={fieldId}
            name={name}
            placeholder={placeholder}
            required={required}
            value={value}
            disabled={disabled}
            onChange={handlePhoneChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );

    case 'radio': {
      // Type guard for string options
      const stringOptions = options as string[];

      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <Radio
            name={name}
            required={required}
            value={value}
            disabled={disabled}
            options={stringOptions}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );
    }

    case 'textarea':
      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <Textarea
            id={fieldId}
            name={name}
            placeholder={placeholder}
            rows={rows || 4}
            required={required}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );

    case 'select': {
      // Type guard for string options
      const stringOptions = options as string[];

      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <Select
            id={fieldId}
            name={name}
            required={required}
            value={value}
            options={stringOptions}
            placeholder={placeholder || 'Choose one...'}
            disabled={disabled}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );
    }

    case 'date':
      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <Date
            id={fieldId}
            name={name}
            placeholder={placeholder}
            required={required}
            unsureCheckboxText={unsureCheckboxText}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );

    case 'range-slider':
      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <RangeSlider
            name={name}
            required={required}
            value={value}
            disabled={disabled}
            options={options as Array<[number, number]>}
            independentMinMax={independentMinMax}
            measure={measure}
            userCountryCode={userCountryCode}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );

    default:
      return (
        <FormGroup
          label={label}
          htmlFor={fieldId}
          required={required}
          index={index}
          error={error}
        >
          <Input
            type={type}
            id={fieldId}
            name={name}
            placeholder={placeholder}
            required={required}
            value={value}
            disabled={disabled}
            onChange={handleChange}
            onBlur={() => onBlur?.(name)}
          />
        </FormGroup>
      );
  }
};
